import type { TFunction } from "i18next";
import { Bell, Bot, BotMessageSquare, EyeOff, Flag, Lock, MessageCircle, MessagesSquare, ShieldAlert, Tag, Trash2, UserPlus, UserRoundSearch, type LucideIcon } from "lucide-react";
import type { Member, Rule, RuleAction, RuleInput } from "@/lib/types";

export type Trigger = Rule["trigger"];
export type Mode = Rule["mode"];
export type ActionType = RuleAction["type"];

export const TRIGGERS: Trigger[] = ["comment.created", "message.received", "mention", "story.reply", "postback"];
export const triggerKey = (t: Trigger) => t.replace(".", "_") as "comment_created" | "message_received" | "mention" | "story_reply" | "postback";

export const MODE_TONE: Record<Mode, "accent" | "warn" | "violet"> = { auto: "accent", approve: "warn", suggest: "violet" };

export const ACTION_TYPES: ActionType[] = ["reply_public", "reply_private", "send_message", "reply_with_agent", "dm_with_agent", "ai_agent_reply", "hide", "delete", "tag", "assign", "notify", "flag", "mark_commenter", "enrich_commenter"];
/** Legacy actions: still rendered (and saved) on existing rules, never offered for new ones. `ai_agent_reply` pointed at a Dify
 *  `dm-agent` app that never existed; it now means "reply with the agent assigned to the conversation / account" (ALP-83). */
export const LEGACY_ACTIONS: ActionType[] = ["ai_agent_reply"];
/** What the "Add action" menu offers: every action that works end to end. */
export const OFFERED_ACTIONS: ActionType[] = ACTION_TYPES.filter((a) => !LEGACY_ACTIONS.includes(a));
/** Notify channels that are delivered today (the worker sends in-app notifications only). */
export const LIVE_NOTIFY_CHANNELS = ["in_app"] as const;

export const ACTION_ICON: Record<ActionType, LucideIcon> = {
  reply_public: MessageCircle,
  reply_private: Lock,
  send_message: MessagesSquare,
  ai_agent_reply: Bot,
  hide: EyeOff,
  delete: Trash2,
  tag: Tag,
  assign: UserPlus,
  notify: Bell,
  flag: Flag,
  mark_commenter: ShieldAlert,
  enrich_commenter: UserRoundSearch,
  reply_with_agent: BotMessageSquare,
  dm_with_agent: BotMessageSquare,
};

/** Which triggers an action makes sense for (comment actions vs DM actions). */
export const COMMENT_ONLY: ActionType[] = ["reply_public", "reply_private", "hide", "delete"];

export const RULE_ICONS = ["💬", "🛡️", "🚨", "🤖", "📘", "🤝", "💙", "✨", "🔑", "⚡", "🎯", "📦", "💰", "🌙"];
export const RULE_COLORS = ["#4f3fe0", "#d1435b", "#a86a06", "#1a9b6c", "#8a35ab", "#0f9bb4", "#d64f7a", "#46435a"];

export const TEMPLATE_VARIABLES = ["{{first_name}}", "{{username}}", "{{post_caption}}", "{{link}}"];

export function defaultAction(type: ActionType, members: Member[] = [], agents: string[] = []): RuleAction {
  switch (type) {
    case "reply_public":
    case "reply_private":
    case "send_message":
      return { type, template: "", aiPersonalize: true };
    case "ai_agent_reply":
      return { type, agent: "dm-agent" };
    case "reply_with_agent":
      return { type, agentId: agents[0] ?? "", private: false };
    case "dm_with_agent":
      return { type, agentId: agents[0] ?? "" };
    case "tag":
      return { type, tags: [] };
    case "assign":
      return { type, assigneeId: members[0]?.user.id ?? "" };
    case "notify":
      return { type, channels: ["in_app"] };
    case "flag":
      return { type, flags: ["follow_up"], target: "item" };
    case "mark_commenter":
      return { type, mark: "harmful" };
    default:
      return { type } as RuleAction;
  }
}

export function emptyRule(accountId: string | null): RuleInput {
  return {
    name: "",
    icon: "✨",
    color: "#4f3fe0",
    scope: { accountId, publicationId: null },
    trigger: "comment.created",
    conditions: { op: "all", of: [{ op: "intent", in: ["price"], minConfidence: 0.75 }] },
    actions: [{ type: "reply_public", template: "", aiPersonalize: true }],
    mode: "approve",
    limits: { perHour: 30, quietHours: null },
    priority: 20,
    isActive: true,
  };
}

export function toInput(r: Rule): RuleInput {
  const { id: _i, stats: _s, createdAt: _c, updatedAt: _u, ...input } = r;
  return input;
}

export function actionSummary(a: RuleAction, t: TFunction, members: Map<string, Member>, agents?: Map<string, string>): string {
  switch (a.type) {
    case "reply_with_agent":
      return t(a.private ? "automation.actions.summaryAgentPrivate" : "automation.actions.summaryAgent", { agent: agents?.get(a.agentId) ?? t("automation.agentPicker.label") });
    case "dm_with_agent":
      return t("automation.actions.summaryAgentDm", { agent: agents?.get(a.agentId) ?? t("automation.agentPicker.label") });
    case "tag":
      return t("automation.actions.summaryTag", { tags: a.tags.map((x) => `#${x}`).join(", ") });
    case "assign":
      return t("automation.actions.summaryAssign", { name: members.get(a.assigneeId)?.user.name ?? "—" });
    case "notify":
      return t("automation.actions.summaryNotify", { channels: a.channels.map((c) => t(`automation.notifyChannels.${c}`)).join(", ") });
    case "flag":
      return t("automation.actions.summaryFlag", { flags: a.flags.join(", ") });
    case "mark_commenter":
      return t("automation.actions.summaryMark", { mark: t(`automation.marks.${a.mark}`) });
    default:
      return t(`automation.actions.${a.type}`);
  }
}

// ---------------------------------------------------------------- draft handoff (prompt / template → builder)
const DRAFT_KEY = "malfak.automation.draft";
export type DraftSource = "prompt" | "template";

export function stashDraft(rule: RuleInput, source: DraftSource) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ rule, source }));
  } catch {
    /* storage unavailable: the builder starts empty */
  }
}

/** Read without consuming (StrictMode runs initializers twice); call clearDraft() after mount. */
export function peekDraft(): { rule: RuleInput; source: DraftSource } | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as { rule: RuleInput; source: DraftSource }) : null;
  } catch {
    return null;
  }
}

export function clearDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to clear */
  }
}
